import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { BookOpen } from 'lucide-react';

const gold = '#C9A227';
const brown = '#3E2723';
const brownMid = '#5a3e10';
const brownLight = '#6D4C41';
const cream = '#FDF6E3';
const creamDark = '#fdf3d0';
const font = 'Frank Ruhl Libre, David, serif';

const GoldDivider = () => (
  <Box sx={{
    height: '1px', my: 1.25,
    background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
    opacity: 0.4,
  }} />
);

const CategoryHeader = ({ label }) => (
  <Box sx={{
    bg: `rgba(201,162,39,0.08)`, borderRadius: '4px',
    px: 1.5, py: 0.5, mb: 0.75, mt: 1,
    backgroundColor: 'rgba(201,162,39,0.08)',
  }}>
    <Typography sx={{ fontFamily: font, fontSize: '0.64rem', fontWeight: 700, color: brownMid, letterSpacing: '0.06em' }}>
      {label}
    </Typography>
  </Box>
);

const LimudCard = ({ labelHe, valueHe, url }) => (
  <Box sx={{
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    p: 1, mb: 0.5,
    bgcolor: 'rgba(62,39,35,0.04)',
    border: '1px solid rgba(201,162,39,0.2)',
    borderRadius: '6px',
    borderRight: `3px solid ${gold}`,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontFamily: font, fontSize: '0.7rem', color: brown, fontWeight: 700 }}>
        {labelHe}
      </Typography>
      {url && <BookOpen size={10} style={{ color: gold, opacity: 0.5, flexShrink: 0, marginTop: 2 }} />}
    </Box>
    <Typography sx={{ fontFamily: font, fontSize: '0.68rem', color: brownMid, fontWeight: 500 }}>
      {valueHe}
    </Typography>
  </Box>
);

/**
 * BeitMidrash — "בית המדרש"
 * קלף חם, מסגרת משולשת, כרטיסים חומים-זהב.
 */
const BeitMidrash = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';
  const displayGroups = groups || [];
  const flatItems = !groups && Array.isArray(items) ? items : null;

  return (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'hidden',
      background: `linear-gradient(180deg, ${cream} 0%, ${creamDark} 100%)`,
      fontFamily: font, direction: 'rtl', position: 'relative',
      p: 2.5,
      // Triple border frame
      '&::before': {
        content: '""', position: 'absolute', inset: '4px',
        border: `2px solid ${gold}`, borderRadius: '3px',
        opacity: 0.5, pointerEvents: 'none',
      },
      '&::after': {
        content: '""', position: 'absolute', inset: '8px',
        border: `1px solid ${gold}`, borderRadius: '2px',
        opacity: 0.25, pointerEvents: 'none',
      },
    }}>
      {/* Innermost border via wrapper */}
      <Box sx={{
        position: 'absolute', inset: '11px',
        border: `1px solid ${gold}`, borderRadius: '1px',
        opacity: 0.15, pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header bar */}
        {showTitle && (
          <Box sx={{
            bgcolor: brown, borderRadius: '4px', px: 2, py: 1, mb: 0.75,
            textAlign: 'center',
          }}>
            <Typography sx={{
              fontFamily: font, fontSize: '0.8rem', fontWeight: 700, color: cream,
              letterSpacing: '0.1em',
            }}>
              ✦ {title} ✦
            </Typography>
          </Box>
        )}

        {hebrewDate && (
          <Typography sx={{ textAlign: 'center', fontFamily: font, fontSize: '0.6rem', color: brownLight, mb: 0.5 }}>
            {hebrewDate}
          </Typography>
        )}

        <GoldDivider />

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 0.5,
          '&::-webkit-scrollbar': { width: '2px' },
          '&::-webkit-scrollbar-thumb': { background: `rgba(201,162,39,0.35)`, borderRadius: '1px' },
        }}>
          {displayGroups.map((group, gi) => (
            <Box key={group.category}>
              {displayGroups.length > 1 && <CategoryHeader label={group.label} />}
              {group.items.map((item) => (
                <LimudCard key={item.key} labelHe={item.labelHe} valueHe={item.valueHe} url={item.url} />
              ))}
              {gi < displayGroups.length - 1 && <GoldDivider />}
            </Box>
          ))}

          {flatItems && flatItems.map((item, i) => (
            <LimudCard key={item.key || i} labelHe={item.labelHe} valueHe={item.valueHe} url={item.url} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BeitMidrash;
